import { SplashLoader } from "@/components/loading";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ALLOWED_EMAIL_DOMAINS } from "@/consts";
import type { LanguageEnum } from "@/i18n";
import { useAuth } from "@/providers/AuthProvider";
import { services } from "@/services";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { CheckCircle, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const Route = createFileRoute("/_public/login")({
  validateSearch: z.object({
    message: z.string().optional(),
  }),
  component: LoginPage,
});

function LoginPage() {
  const { user, isLoading } = useAuth();
  const { t, i18n } = useTranslation();
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const { message } = Route.useSearch();

  const emailSchema = z.object({
    email: z
      .string()
      .email(t("login.invalidEmail"))
      .refine((email) => ALLOWED_EMAIL_DOMAINS.includes(email.split("@")[1]?.toLowerCase()), {
        message: t("login.emailDomainNotAllowed"),
      }),
  });

  const { mutate: sendMagicLink, isPending } = useMutation({
    mutationFn: async (email: string) => {
      await services.login(email.trim(), i18n.language as LanguageEnum);
      return email;
    },
    onSuccess: (email) => setSuccessEmail(email),
  });

  const form = useForm({
    defaultValues: { email: "" },
    onSubmit: async ({ value }) => sendMagicLink(value.email),
    validators: { onChange: emailSchema },
  });

  if (isLoading) return <SplashLoader />;
  if (user) return <Navigate to="/" replace />;

  if (successEmail) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 py-16 lg:py-24">
        <Card className="w-full max-w-sm py-0">
          <CardContent className="px-6 py-8 flex flex-col items-center text-center gap-6">
            <div className="p-3 rounded-full bg-muted text-primary">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-bold tracking-tight">{t("login.checkYourEmail")}</h1>
              <p className="text-sm text-muted-foreground">
                {t("login.magicLinkSent")}{" "}
                <span className="font-medium text-foreground">{successEmail}</span>.
              </p>
            </div>
            <p className="text-xs text-muted-foreground bg-muted/50 px-4 py-2 rounded-lg border w-full">
              {t("login.clickLinkToSignIn")}
            </p>
            <Button
              onClick={() => {
                setSuccessEmail(null);
                form.reset();
              }}
              variant="outline"
              className="w-full"
            >
              {t("login.useDifferentEmail")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 py-16 lg:py-24">
      <Card className="w-full max-w-sm py-0">
        <CardContent className="px-6 py-8 flex flex-col items-center text-center gap-6">
          <img src="/logo.svg" alt="Logo" className="w-12 h-12 object-contain" />
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">{t("login.welcomeBack")}</h1>
            <p className="text-sm text-muted-foreground">{t("login.enterEmailForMagicLink")}</p>
          </div>
          {message && (
            <p className="text-xs font-medium px-3 py-2 rounded-lg border border-destructive/20 bg-destructive/5 text-destructive w-full">
              {t(`login.${message}`)}
            </p>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4 w-full"
          >
            <div className="flex flex-col gap-1.5 text-start">
              <Label
                htmlFor="email"
                className="text-xs font-mono uppercase tracking-wider text-muted-foreground"
              >
                {t("login.email")}
              </Label>
              <form.Field
                name="email"
                validators={{ onChange: z.string().email(t("login.invalidEmail")) }}
                children={(field) => (
                  <div className="flex flex-col gap-1.5">
                    <div className="relative">
                      <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input
                        id={field.name}
                        name={field.name}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder={t("login.enterYourEmail")}
                        type="email"
                        required
                        disabled={isPending}
                        className="ps-9"
                      />
                    </div>
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-xs text-destructive">
                        {field.state.meta.errors.map((err) => err?.message).join(", ")}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
            <Button
              type="submit"
              disabled={isPending || !form.state.isFormValid}
              className="w-full"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Mail className="h-4 w-4" /> {t("login.sendMagicLink")}
                </>
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground">
            <Link to="/terms" className="text-primary hover:underline">
              {t("login.termsOfService")}
            </Link>
            {" · "}
            <Link to="/privacy" className="text-primary hover:underline">
              {t("login.privacyPolicy")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
